/**
 * AreasTab — areas for a machine + part instances within each area.
 *
 * Areas are physical zones on the machine. Inside an area, part INSTANCES are
 * created by selecting a MachinePartType (the reusable "kind"); name/partNumber
 * default from the kind but remain editable. Rendered inside MachineDetailPage.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DrawerPanel, ConfirmDialog, GuidanceImage, ImageUpload } from '../../components/shared';
import type { DrawerAction } from '../../components/shared';
import { MachineControllerApi, MachinePartTypeControllerApi } from '../../api-generated/apis';
import type { Area, MachinePart, MachinePartType } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const machineApi = new MachineControllerApi(apiConfig);
const partTypeApi = new MachinePartTypeControllerApi(apiConfig);

interface AreasTabProps {
  machineId: string;
}

const AreasTab: React.FC<AreasTabProps> = ({ machineId }) => {
  const queryClient = useQueryClient();

  // Area drawer
  const [areaDrawerOpen, setAreaDrawerOpen] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null);
  const [areaName, setAreaName] = useState('');
  const [areaDescription, setAreaDescription] = useState('');
  const [areaImageUrl, setAreaImageUrl] = useState('');

  // Part-instance drawer
  const [partDrawerOpen, setPartDrawerOpen] = useState(false);
  const [partAreaId, setPartAreaId] = useState<string | null>(null);
  const [partTypeId, setPartTypeId] = useState('');
  const [partName, setPartName] = useState('');
  const [partNumber, setPartNumber] = useState('');

  // Confirm delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteAreaId, setDeleteAreaId] = useState<string | null>(null);

  const areasQuery = useQuery<Area[]>({
    queryKey: ['areas', machineId],
    queryFn: () => machineApi.getAreas({ id: machineId }),
    enabled: !!machineId,
  });

  const partTypesQuery = useQuery<MachinePartType[]>({
    queryKey: ['machine-part-types'],
    queryFn: () => partTypeApi.getAllMachinePartTypes(),
    staleTime: 60_000,
  });

  const areas = areasQuery.data ?? [];

  // ─── Area mutations ──────────────────────────────────────────────────────
  const saveAreaMutation = useMutation({
    mutationFn: (data: { id?: string; body: Area }) => {
      if (data.id) {
        return machineApi.updateArea({ id: machineId, areaId: data.id, area: data.body });
      }
      return machineApi.createArea({ id: machineId, area: data.body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas', machineId] });
      setAreaDrawerOpen(false);
    },
  });

  const deleteAreaMutation = useMutation({
    mutationFn: (areaId: string) => machineApi.deleteArea({ id: machineId, areaId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas', machineId] });
      setConfirmOpen(false);
    },
  });

  // ─── Part-instance mutation ──────────────────────────────────────────────
  const addPartMutation = useMutation({
    mutationFn: (data: { areaId: string; body: MachinePart }) =>
      machineApi.addPart({ id: machineId, machinePart: data.body }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['parts-in-area', vars.areaId] });
      setPartDrawerOpen(false);
    },
  });

  // ─── Area handlers ───────────────────────────────────────────────────────
  const openAddArea = useCallback(() => {
    setEditingAreaId(null);
    setAreaName('');
    setAreaDescription('');
    setAreaImageUrl('');
    setAreaDrawerOpen(true);
  }, []);

  const openEditArea = useCallback((area: Area) => {
    setEditingAreaId(area.id ?? null);
    setAreaName(area.name ?? '');
    setAreaDescription(area.description ?? '');
    setAreaImageUrl(area.imageUrl ?? '');
    setAreaDrawerOpen(true);
  }, []);

  const handleSaveArea = useCallback(() => {
    if (!areaName.trim()) return;
    saveAreaMutation.mutate({
      id: editingAreaId ?? undefined,
      body: {
        name: areaName.trim(),
        description: areaDescription.trim() || undefined,
        imageUrl: areaImageUrl.trim() || undefined,
        machineId,
      },
    });
  }, [areaName, areaDescription, areaImageUrl, editingAreaId, machineId, saveAreaMutation]);

  // ─── Part handlers ───────────────────────────────────────────────────────
  const openAddPart = useCallback((areaId: string) => {
    setPartAreaId(areaId);
    setPartTypeId('');
    setPartName('');
    setPartNumber('');
    setPartDrawerOpen(true);
  }, []);

  const handlePartTypeChange = useCallback(
    (typeId: string) => {
      setPartTypeId(typeId);
      const pt = (partTypesQuery.data ?? []).find((t: MachinePartType) => t.id === typeId);
      if (pt) {
        setPartName(pt.name ?? '');
        setPartNumber(pt.partNumber ?? '');
      }
    },
    [partTypesQuery.data],
  );

  const handleSavePart = useCallback(() => {
    if (!partAreaId || !partName.trim()) return;
    addPartMutation.mutate({
      areaId: partAreaId,
      body: {
        name: partName.trim(),
        partNumber: partNumber.trim() || undefined,
        areaId: partAreaId,
        machinePartTypeId: partTypeId || undefined,
        machineId,
      } as any,
    });
  }, [partAreaId, partName, partNumber, partTypeId, machineId, addPartMutation]);

  const areaDrawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setAreaDrawerOpen(false), testId: 'area-form-cancel' },
    { label: editingAreaId ? 'Update' : 'Create', variant: 'contained', onClick: handleSaveArea, disabled: !areaName, testId: 'area-form-save' },
  ];

  const partDrawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setPartDrawerOpen(false), testId: 'area-part-form-cancel' },
    { label: 'Add Part', variant: 'contained', onClick: handleSavePart, disabled: !partName || addPartMutation.isPending, testId: 'area-part-form-save' },
  ];

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Areas</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAddArea} data-testid="area-add-btn">
          Add Area
        </Button>
      </Stack>

      {areasQuery.isLoading && <Typography color="text.secondary">Loading…</Typography>}
      {!areasQuery.isLoading && areas.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No areas defined. Add an area to group part instances.
        </Typography>
      )}

      {areas.map((area: Area) => (
        <Accordion key={area.id} disableGutters data-testid={`area-row-${area.id}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, pr: 1 }}>
              <Typography sx={{ flex: 1 }}>{area.name}</Typography>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditArea(area); }} data-testid={`area-edit-${area.id}`}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => { e.stopPropagation(); setDeleteAreaId(area.id ?? null); setConfirmOpen(true); }}
                data-testid={`area-delete-${area.id}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {area.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {area.description}
              </Typography>
            )}
            {area.imageUrl && (
              <Box sx={{ mb: 1.5, maxWidth: 320 }}>
                <GuidanceImage imageUrl={area.imageUrl} altText={area.name ?? 'Area'} />
              </Box>
            )}
            <AreaParts areaId={area.id!} machineId={machineId} />
            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => openAddPart(area.id!)} data-testid={`area-add-part-${area.id}`}>
              Add Part Instance
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Area drawer */}
      <DrawerPanel
        open={areaDrawerOpen}
        onClose={() => setAreaDrawerOpen(false)}
        title={editingAreaId ? 'Edit Area' : 'New Area'}
        width="narrow"
        actions={areaDrawerActions}
        testId="area-form"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField label="Name" value={areaName} onChange={(e) => setAreaName(e.target.value)} required fullWidth inputProps={{ 'data-testid': 'area-form-name' }} />
          <TextField label="Description" value={areaDescription} onChange={(e) => setAreaDescription(e.target.value)} fullWidth multiline rows={2} inputProps={{ 'data-testid': 'area-form-description' }} />
          <ImageUpload
            label="Area Image"
            value={areaImageUrl}
            onChange={setAreaImageUrl}
            purpose="area"
            helperText="Upload an image of this area."
          />
        </Box>
      </DrawerPanel>

      {/* Part-instance drawer */}
      <DrawerPanel
        open={partDrawerOpen}
        onClose={() => setPartDrawerOpen(false)}
        title="Add Part Instance"
        width="narrow"
        actions={partDrawerActions}
        testId="area-part-form"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Part Type (kind)"
            value={partTypeId}
            onChange={(e) => handlePartTypeChange(e.target.value)}
            select
            fullWidth
            disabled={partTypesQuery.isLoading}
            helperText="Select the reusable part kind"
            SelectProps={{ SelectDisplayProps: { 'data-testid': 'area-part-form-type' } as any }}
          >
            <MenuItem value="">— None —</MenuItem>
            {(partTypesQuery.data ?? []).map((pt: MachinePartType) => (
              <MenuItem key={pt.id} value={pt.id ?? ''}>
                {pt.name}{pt.partNumber ? ` (${pt.partNumber})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Name" value={partName} onChange={(e) => setPartName(e.target.value)} required fullWidth inputProps={{ 'data-testid': 'area-part-form-name' }} />
          <TextField label="Part Number" value={partNumber} onChange={(e) => setPartNumber(e.target.value)} fullWidth inputProps={{ 'data-testid': 'area-part-form-number' }} />
        </Box>
      </DrawerPanel>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deleteAreaId && deleteAreaMutation.mutate(deleteAreaId)}
        title="Delete Area"
        message="Delete this area? Part instances in it will be orphaned."
        confirmLabel="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

// ─── Part instances within one area ──────────────────────────────────────────

const AreaParts: React.FC<{ areaId: string; machineId: string }> = ({ areaId, machineId }) => {
  const partsQuery = useQuery<MachinePart[]>({
    queryKey: ['parts-in-area', areaId],
    queryFn: () => machineApi.getPartsInArea({ id: machineId, areaId }),
    enabled: !!areaId,
  });

  const parts = partsQuery.data ?? [];

  if (partsQuery.isLoading) return <Typography variant="caption" color="text.secondary">Loading parts…</Typography>;
  if (parts.length === 0) return <Typography variant="body2" color="text.secondary">No part instances in this area.</Typography>;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {parts.map((p: MachinePart) => (
        <Chip key={p.id} label={`${p.name}${p.partNumber ? ` · ${p.partNumber}` : ''}`} size="small" variant="outlined" />
      ))}
    </Stack>
  );
};

export default AreasTab;
